import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, useParams, useSearchParams } from "react-router-dom";

import { ModuleRoute } from "@/components/auth";
import { sanitizeInternalNextPath } from "@/lib/auth/protected-routes";
import { AccessPendingPage } from "@/src/auth/access-pending-page";
import { AcceptInvitePage } from "@/src/auth/accept-invite-page";
import { ForgotPasswordPage } from "@/src/auth/forgot-password-page";
import { ResetPasswordPage } from "@/src/auth/reset-password-page";
import { LoginClient } from "@/src/login/login-client";
import { ScrollToTop } from "@/src/scroll-to-top";
import { WorkspaceShell } from "@/src/shell/workspace-shell";
import { AlertDetailPage } from "@/modules/alerts/pages/alert-detail-page";
import { ActivityPolicyPage } from "@/modules/activity-policy";
import { RuleNewPage } from "@/modules/alerts/pages/rule-new-page";
import { AnalyticsPage } from "@/modules/analytics";
import { BillingPage } from "@/modules/billing";
import { DataloggerCreatePage } from "@/modules/core/datalogger-create";
import { EnterpriseDashboardPage } from "@/modules/dashboard/pages/enterprise-dashboard-page";
import { DashboardCanvasPage, DashboardListPage } from "@/modules/dashboards";
import { BackupDownloadPage, DataManagementPage } from "@/modules/data-management";
import { EnterpriseDevicesListPage } from "@/modules/devices/pages/enterprise-devices-list-page";
import { DeviceDetailPage } from "@/modules/devices/pages/device-detail-page";
import { DeviceExternalAccessPage } from "@/modules/devices/pages/device-external-access-page";
import { DeviceManagementPage } from "@/modules/devices/pages/device-management-page";
import { DeviceMasterViewPage } from "@/modules/devices/pages/device-master-view-page";
import { DataExplorerPage } from "@/modules/explorer";
import { LiveMonitoringPage } from "@/modules/live";
import { NotificationsPage } from "@/modules/notifications/pages/notifications-page";
import { AutomationsPage } from "@/modules/automations";
import { OrganizationsPage } from "@/modules/organizations";
import { OtaDetailPage, OtaFormPage, OtaPage } from "@/modules/ota";
import { PermissionsPage } from "@/modules/permissions";
import { PlatformRolesPage } from "@/modules/platform-roles";
import { PlatformUsersPage } from "@/modules/platform-users";
import { DeviceCategoriesPage } from "@/modules/device-categories/pages/device-categories-page";
import { ExpenseCategoriesPage } from "@/modules/expense-categories/pages/expense-categories-page";
import { ExpensesPage } from "@/modules/expenses/pages/expenses-page";
import { PublicDevicePage } from "@/modules/devices/pages/public-device-page";
import { EntitlementsPage } from "@/modules/entitlements";
import { ProfilePage } from "@/modules/profile/pages/profile-page";
import { ReportsPage } from "@/modules/reports";
import { ReportResultsPage } from "@/modules/report-results";
import { SecurityPage } from "@/modules/security";
import { SettingsIndexRedirect, SettingsPage } from "@/modules/settings";
import { SupportPage, SupportTicketDetailPage } from "@/modules/support";
import { AnnouncementEditorPage, AnnouncementsPage } from "@/modules/announcements";
import { AnnouncementsListPage } from "@/modules/announcements/pages/announcements-list-page";
import { SystemHealthPage } from "@/modules/system-health";
import { TenantsPage } from "@/modules/tenants";
import { UsersPage } from "@/modules/users";
import { useAuthStore } from "@/modules/auth/store";

const AlertsHubPage = lazy(() =>
  import("@/modules/alerts/pages/alerts-hub-page").then((m) => ({ default: m.AlertsHubPage })),
);

function WorkspaceLayout() {
  return (
    <WorkspaceShell>
      <Outlet />
    </WorkspaceShell>
  );
}

function AnnouncementsRoute() {
  const user = useAuthStore((s) => s.user);
  const platformAccount = user?.role === "super_admin" || user?.accountType === "platform";
  return platformAccount ? <ModuleRoute moduleKey="announcements"><AnnouncementsPage /></ModuleRoute> : <AnnouncementsPage />;
}

function AnnouncementsListRoute() {
  const user = useAuthStore((s) => s.user);
  const platformAccount = user?.role === "super_admin" || user?.accountType === "platform";
  return platformAccount ? <ModuleRoute moduleKey="announcements"><AnnouncementsListPage /></ModuleRoute> : <AnnouncementsPage />;
}

function AnnouncementEditorRoute({ action }: { action: "create" | "edit" }) {
  const user = useAuthStore((s) => s.user);
  const platformAccount = user?.role === "super_admin" || user?.accountType === "platform";
  return platformAccount ? <ModuleRoute moduleKey="announcements" action={action}><AnnouncementEditorPage /></ModuleRoute> : <Navigate to="/announcements" replace />;
}

function LoginRoute() {
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get("next");
  const nextPath = sanitizeInternalNextPath(rawNext ?? undefined);
  const authErrorFromRedirect = searchParams.get("auth_error") === "1";
  return <LoginClient nextPath={nextPath} authErrorFromRedirect={authErrorFromRedirect} />;
}

function DeviceDetailRoute() {
  const { deviceId } = useParams<{ deviceId: string }>();
  if (!deviceId) {
    return <Navigate to="/devices" replace />;
  }
  return <DeviceDetailPage deviceId={deviceId} mode="view" />;
}

function DeviceEditRedirect() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const [searchParams] = useSearchParams();
  if (!deviceId) {
    return <Navigate to="/devices" replace />;
  }
  const initialTab = searchParams.get("tab") ?? undefined;
  return <DeviceDetailPage deviceId={deviceId} mode="edit" initialTab={initialTab} />;
}

function DeviceExternalAccessRoute() {
  const { deviceId } = useParams<{ deviceId: string }>();
  if (!deviceId) {
    return <Navigate to="/devices" replace />;
  }
  return <DeviceExternalAccessPage deviceId={deviceId} />;
}

function PublicDeviceRoute() {
  const { token } = useParams<{ token: string }>();
  return token ? <PublicDevicePage token={token} /> : <Navigate to="/" replace />;
}

function NotificationsHubRoute() {
  return (
    <Suspense fallback={<div className="p-8 text-sm text-foreground/55">Loading notifications...</div>}>
      <AlertsHubPage />
    </Suspense>
  );
}

function AlertHistoryDetailRoute() {
  const { alertId } = useParams<{ alertId: string }>();
  if (!alertId) {
    return <Navigate to="/alerts/history" replace />;
  }
  return <AlertDetailPage />;
}

function LegacyNotificationDetailRedirect() {
  const { alertId } = useParams<{ alertId: string }>();
  return <Navigate to={alertId ? `/alerts/history/${alertId}` : "/alerts/history"} replace />;
}

function DashboardCanvasRoute() {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  if (!dashboardId) {
    return <Navigate to="/dashboards" replace />;
  }
  return <DashboardCanvasPage dashboardId={dashboardId} />;
}

function DashboardModuleGate({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (user?.role === "super_admin" || user?.accountType === "platform") {
    return <Navigate to="/overview" replace />;
  }
  return children;
}

function OtaEditRoute() {
  const { campaignId } = useParams<{ campaignId: string }>();
  if (!campaignId) {
    return <Navigate to="/ota" replace />;
  }
  return <OtaFormPage campaignId={campaignId} />;
}

function OtaDetailRoute() {
  const { campaignId } = useParams<{ campaignId: string }>();
  if (!campaignId) {
    return <Navigate to="/ota" replace />;
  }
  return <OtaDetailPage campaignId={campaignId} />;
}

export function AppRouter() {
  return (
    <Router basename="/">
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LoginRoute />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/public/devices/:token" element={<PublicDeviceRoute />} />

        <Route element={<WorkspaceLayout />}>
          <Route path="/access-pending" element={<AccessPendingPage />} />
          <Route path="/overview" element={<ModuleRoute moduleKey="overview"><EnterpriseDashboardPage /></ModuleRoute>} />
          <Route path="/fleet" element={<ModuleRoute moduleKey="fleet"><EnterpriseDevicesListPage /></ModuleRoute>} />
          <Route path="/dashboard" element={<Navigate to="/fleet" replace />} />

          <Route path="/devices" element={<ModuleRoute moduleKey="devices"><DeviceManagementPage /></ModuleRoute>} />
          <Route path="/devices/new" element={<ModuleRoute moduleKey="devices" action="create"><DataloggerCreatePage /></ModuleRoute>} />
          <Route path="/devices/master-view" element={<ModuleRoute moduleKey="master_view"><DeviceMasterViewPage /></ModuleRoute>} />
          <Route path="/devices/:deviceId" element={<ModuleRoute moduleKey="devices"><DeviceDetailRoute /></ModuleRoute>} />
          <Route path="/devices/:deviceId/edit" element={<ModuleRoute moduleKey="devices" action="edit"><DeviceEditRedirect /></ModuleRoute>} />
          <Route path="/devices/:deviceId/external-access" element={<ModuleRoute moduleKey="devices" action="edit"><DeviceExternalAccessRoute /></ModuleRoute>} />

          <Route path="/alerts" element={<ModuleRoute moduleKey="automations"><AutomationsPage /></ModuleRoute>} />
          <Route path="/alerts/rules/new" element={<ModuleRoute moduleKey="automations" action="create"><RuleNewPage /></ModuleRoute>} />
          <Route path="/alerts/history" element={<ModuleRoute moduleKey="alerts"><NotificationsHubRoute /></ModuleRoute>} />
          <Route path="/alerts/history/:alertId" element={<ModuleRoute moduleKey="alerts"><AlertHistoryDetailRoute /></ModuleRoute>} />
          <Route path="/notifications" element={<ModuleRoute moduleKey="alerts"><NotificationsPage /></ModuleRoute>} />
          <Route path="/notifications/:alertId" element={<LegacyNotificationDetailRedirect />} />
          <Route path="/announcements" element={<AnnouncementsListRoute />} />
          <Route path="/announcements/new" element={<AnnouncementEditorRoute action="create" />} />
          <Route path="/announcements/:announcementId/edit" element={<AnnouncementEditorRoute action="edit" />} />
          <Route path="/announcements/:announcementId" element={<AnnouncementsRoute />} />

          <Route path="/live" element={<ModuleRoute moduleKey="live"><LiveMonitoringPage /></ModuleRoute>} />
          <Route path="/explorer" element={<ModuleRoute moduleKey="explorer"><DataExplorerPage /></ModuleRoute>} />
          <Route path="/telemetry" element={<Navigate to="/live" replace />} />
          <Route path="/commands" element={<Navigate to="/devices" replace />} />

          <Route path="/analytics" element={<ModuleRoute moduleKey="analytics"><AnalyticsPage /></ModuleRoute>} />
          <Route path="/dashboards" element={<DashboardModuleGate><ModuleRoute moduleKey="dashboards"><DashboardListPage /></ModuleRoute></DashboardModuleGate>} />
          <Route path="/dashboards/:dashboardId" element={<DashboardModuleGate><ModuleRoute moduleKey="dashboards"><DashboardCanvasRoute /></ModuleRoute></DashboardModuleGate>} />
          <Route path="/ota" element={<ModuleRoute moduleKey="ota"><OtaPage /></ModuleRoute>} />
          <Route path="/ota/new" element={<ModuleRoute moduleKey="ota" action="create"><OtaFormPage /></ModuleRoute>} />
          <Route path="/ota/:campaignId" element={<ModuleRoute moduleKey="ota"><OtaDetailRoute /></ModuleRoute>} />
          <Route path="/ota/:campaignId/edit" element={<ModuleRoute moduleKey="ota" action="edit"><OtaEditRoute /></ModuleRoute>} />
          <Route path="/automations" element={<Navigate to="/alerts" replace />} />
          <Route path="/reports" element={<ModuleRoute moduleKey="reports"><ReportsPage /></ModuleRoute>} />
          <Route path="/reports/channels" element={<ModuleRoute moduleKey="reports.channels"><ReportResultsPage reportKey="channels" /></ModuleRoute>} />
          <Route path="/reports/users" element={<ModuleRoute moduleKey="reports.users"><ReportResultsPage reportKey="users" /></ModuleRoute>} />
          <Route path="/reports/sales/time" element={<ModuleRoute moduleKey="reports.sales.time"><ReportResultsPage reportKey="sales-time" /></ModuleRoute>} />
          <Route path="/reports/sales/region" element={<ModuleRoute moduleKey="reports.sales.region"><ReportResultsPage reportKey="sales-region" /></ModuleRoute>} />
          <Route path="/reports/dataloggers/warranty" element={<ModuleRoute moduleKey="reports.dataloggers.warranty"><ReportResultsPage reportKey="warranty" /></ModuleRoute>} />
          <Route path="/reports/alerts" element={<ModuleRoute moduleKey="reports.alerts"><ReportResultsPage reportKey="alerts" /></ModuleRoute>} />
          <Route path="/reports/support/tickets" element={<ModuleRoute moduleKey="reports.support.tickets"><ReportResultsPage reportKey="support-tickets" /></ModuleRoute>} />
          <Route path="/reports/data/usage" element={<ModuleRoute moduleKey="reports.data.usage"><ReportResultsPage reportKey="data-usage" /></ModuleRoute>} />
          <Route path="/reports/expenses" element={<ModuleRoute moduleKey="expenses"><ExpensesPage /></ModuleRoute>} />
          <Route path="/users" element={<ModuleRoute moduleKey="users"><UsersPage /></ModuleRoute>} />
          <Route path="/platform-users" element={<ModuleRoute moduleKey="platform_users"><PlatformUsersPage /></ModuleRoute>} />
          <Route path="/platform-roles" element={<ModuleRoute moduleKey="platform_roles"><PlatformRolesPage /></ModuleRoute>} />
          <Route path="/device-categories" element={<ModuleRoute moduleKey="device_categories"><DeviceCategoriesPage /></ModuleRoute>} />
          <Route path="/expense-categories" element={<ModuleRoute moduleKey="expense_categories"><ExpenseCategoriesPage /></ModuleRoute>} />
          <Route path="/expenses" element={<Navigate to="/reports/expenses" replace />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/tenants" element={<ModuleRoute moduleKey="tenants"><TenantsPage /></ModuleRoute>} />
          <Route path="/permissions" element={<ModuleRoute moduleKey="roles"><PermissionsPage /></ModuleRoute>} />
          <Route path="/settings" element={<SettingsPage />}>
            <Route index element={<SettingsIndexRedirect />} />
            <Route path="entitlements" element={<ModuleRoute moduleKey="entitlements" fallback="/settings"><EntitlementsPage /></ModuleRoute>} />
            <Route path="activity-policy" element={<ModuleRoute moduleKey="activity_policy" fallback="/settings"><ActivityPolicyPage /></ModuleRoute>} />
          </Route>
          <Route path="/entitlements" element={<Navigate to="/settings/entitlements" replace />} />
          <Route path="/activity-policy" element={<Navigate to="/settings/activity-policy" replace />} />
          <Route path="/tenant" element={<Navigate to="/tenants" replace />} />
          <Route path="/organizations" element={<ModuleRoute moduleKey="locations"><OrganizationsPage /></ModuleRoute>} />
          <Route path="/billing" element={<ModuleRoute moduleKey="billing"><BillingPage /></ModuleRoute>} />
          <Route path="/support" element={<ModuleRoute moduleKey="support"><SupportPage /></ModuleRoute>} />
          <Route path="/support/:ticketId" element={<ModuleRoute moduleKey="support"><SupportTicketDetailPage /></ModuleRoute>} />
          <Route path="/data-management" element={<ModuleRoute moduleKey="data_management"><DataManagementPage /></ModuleRoute>} />
          <Route path="/data-management/backups/:backupId/download" element={<BackupDownloadPage />} />
          <Route path="/security" element={<ModuleRoute moduleKey="security"><SecurityPage /></ModuleRoute>} />
          <Route path="/system-health" element={<ModuleRoute moduleKey="system_health"><SystemHealthPage /></ModuleRoute>} />
        </Route>

        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Routes>
    </Router>
  );
}
